const QUERY_PREFIX_RE = /^(why|how|what|when|where|who|which|should|can|could|would|will|is|are|do|does|did)\b/i

const normalizeComparableText = (text: string): string =>
  cleanSummaryPreview(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

export function cleanSummaryPreview(text: string): string {
  if (!text) return ''
  return text
    .replace(/^\*\*Executive Summary\*\*:?\s*/i, '')
    .replace(/^Executive Summary:?\s*/i, '')
    .replace(/\*\s*Key\s*findings?\s*:?.*$/i, '')
    .replace(/[_*`>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function extractSummaryFromReport(reportText: string): string {
  if (!reportText) return ''

  const normalized = reportText.replace(/\r\n/g, '\n')
  const execIndex = normalized.toLowerCase().indexOf('executive summary')
  let snippetSource = execIndex >= 0 ? normalized.slice(execIndex + 'executive summary'.length) : normalized
  snippetSource = snippetSource.trim()
  snippetSource = snippetSource.split(/\n{2,}/)[0] || snippetSource
  snippetSource = snippetSource.replace(/[#>*_`]/g, ' ').replace(/\s+/g, ' ').trim()

  if (!snippetSource) return ''

  const sentences = snippetSource.split(/(?<=[.!?])\s+/).filter(Boolean)
  return sentences.slice(0, 3).join(' ').trim()
}

export function isLikelyUserQueryPreview(text: string, query?: string, title?: string): boolean {
  const cleaned = cleanSummaryPreview(text).toLowerCase()
  const cleanedQuery = cleanSummaryPreview(query || '').toLowerCase()
  const cleanedTitle = cleanSummaryPreview(title || '').toLowerCase()
  const normalized = normalizeComparableText(text)
  const normalizedQuery = normalizeComparableText(query || '')
  const normalizedTitle = normalizeComparableText(title || '')

  if (!cleaned) return false
  if (cleanedQuery && cleaned === cleanedQuery) return true
  if (cleanedTitle && cleaned === cleanedTitle) return true
  if (normalizedQuery && normalized === normalizedQuery) return true
  if (normalizedTitle && normalized === normalizedTitle) return true
  return cleaned.includes('?') || QUERY_PREFIX_RE.test(cleaned)
}

export function sanitizeTopicSummary(text: string, query?: string, title?: string): string {
  const cleaned = cleanSummaryPreview(text)
  if (!cleaned) return ''
  return isLikelyUserQueryPreview(cleaned, query, title) ? '' : cleaned
}

export function resolveTopicPreviewSummary(topic: {
  summary?: string
  reportText?: string
  query?: string
  title: string
}): string {
  const sanitizedSummary = sanitizeTopicSummary(topic.summary || '', topic.query, topic.title)
  if (sanitizedSummary) {
    return sanitizedSummary
  }

  const extractedReportSummary = extractSummaryFromReport(topic.reportText || '')
  return sanitizeTopicSummary(extractedReportSummary, topic.query, topic.title)
}

export function getTopicPreviewText(topic: { summary?: string; description?: string; query?: string; title: string }, maxLength = 360): string {
  const raw = sanitizeTopicSummary(
    (topic.summary && topic.summary.trim()) || (topic.description && topic.description.trim()) || '',
    topic.query,
    topic.title
  )

  if (!raw) {
    return 'Preview the executive summary and key findings from this trending Insight Atlas research.'
  }

  const sentences = raw.match(/[^.!?]+[.!?]+/g) || [raw]
  const combined = sentences.slice(0, 3).join(' ').trim()
  if (combined.length <= maxLength) {
    return combined
  }
  return `${combined.slice(0, maxLength - 1).trim()}…`
}
